"use client";

import { memo } from "react";
import { cn } from "@/lib/utils";
import LocationCombobox, {
  type LocationComboboxOption,
} from "@/components/LocationCombobox";
import { ErrorTooltip } from "@/components/ErrorTooltip";

type SearchLocationFieldProps = {
  isHotel: boolean;
  isTour?: boolean;
  value: string;
  hasCoordinates: boolean;
  isFetchingLocation: boolean;
  hasError?: boolean;
  showCurrentLocation?: boolean;
  isLoadingOptions: boolean;
  options: LocationComboboxOption[];
  sectionTitle: string;
  isInline?: boolean;
  useInlineFieldExpansion?: boolean;
  inlineExpanded?: boolean;
  inlineFloating?: boolean;
  onChange: (value: string, option?: LocationComboboxOption) => void;
  onInputChange: (value: string) => void;
  onUseCurrentLocation: () => void;
  onInlineExpandedChange?: (open: boolean) => void;
};

const SearchLocationField = ({
  isHotel,
  isTour = false,
  value,
  hasCoordinates,
  isFetchingLocation,
  hasError,
  showCurrentLocation,
  isLoadingOptions,
  options,
  sectionTitle,
  isInline,
  useInlineFieldExpansion,
  inlineExpanded = false,
  inlineFloating = false,
  onChange,
  onInputChange,
  onUseCurrentLocation,
  onInlineExpandedChange,
}: SearchLocationFieldProps) => {
  const resolvedIsInline =
    typeof isInline === "boolean"
      ? isInline
      : typeof useInlineFieldExpansion === "boolean"
        ? useInlineFieldExpansion
        : typeof onInlineExpandedChange === "function";

  return (
    <div className="flex-1 min-w-0 relative">
      <ErrorTooltip
        show={Boolean(hasError)}
        message={
          isHotel
            ? "Please provide a location or hotel name"
            : isTour
              ? "Please provide a destination or keyword"
              : "Please provide a location"
        }
      />
      <LocationCombobox
        className={cn(
          hasError &&
            "border-error-foreground bg-error-background ring-1 ring-error-border transition-colors",
        )}
        value={value}
        onChange={onChange}
        onInputChange={onInputChange}
        onUseCurrentLocation={onUseCurrentLocation}
        showCurrentLocation={showCurrentLocation}
        isCurrentLocationSelected={hasCoordinates}
        isUsingCurrentLocation={isFetchingLocation}
        isLoadingOptions={isLoadingOptions}
        options={options}
        placeholder={
          isHotel
            ? "Search hotels, cities, or destinations"
            : isTour
              ? "Search activities, destinations, or keywords"
              : "Location"
        }
        sectionTitle={sectionTitle}
        isInline={resolvedIsInline}
        inlineExpanded={inlineExpanded}
        inlineFloating={inlineFloating}
        onInlineExpandedChange={
          resolvedIsInline ? onInlineExpandedChange : undefined
        }
      />
    </div>
  );
};

export default memo(SearchLocationField);
